import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { UsersApiAdapter } from '../adapters/UsersApiAdapter';
import { Service } from '../services/Service';

const PENDING_INVITATIONS_QUERY_KEY = ['pending-invitations'] as const;

/**
 * Hook para reenviar una invitación
 */
export const useResendInvitation = () => {
  const { t } = useTranslation('usersModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const httpClient = createHttpClient(apiClientWH);
  const usersAdapter = new UsersApiAdapter(httpClient);
  const usersService = new Service(usersAdapter);

  return useMutation({
    mutationFn: (invitationId: number) => usersService.resendInvitation(invitationId),
    onSuccess: () => {
      // Invalidar query de invitaciones pendientes para refrescar la lista
      queryClient.invalidateQueries({ queryKey: PENDING_INVITATIONS_QUERY_KEY });

      // Mostrar toast de éxito
      toast({
        title: t('invitations.success.resend', 'Invitación reenviada exitosamente'),
        description: t(
          'invitations.success.resendDesc',
          'El email de invitación ha sido reenviado al usuario'
        ),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      console.error('Error al reenviar invitación:', error);

      // Extraer mensaje de error
      let errorMessage = t('invitations.errors.resendFailed', 'Error al reenviar invitación');

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // Mensajes específicos según el tipo de error
      if (errorMessage.includes('ya aceptada') || errorMessage.includes('already accepted')) {
        errorMessage = t(
          'invitations.errors.alreadyAccepted',
          'Esta invitación ya fue aceptada'
        );
      } else if (errorMessage.includes('no encontrada') || errorMessage.includes('not found')) {
        errorMessage = t('invitations.errors.notFound', 'Invitación no encontrada');
      } else if (errorMessage.includes('permisos') || errorMessage.includes('permission')) {
        errorMessage = t(
          'invitations.errors.noPermission',
          'No tienes permisos para realizar esta acción'
        );
      }

      toast({
        title: t('invitations.errors.resendFailed', 'Error al reenviar invitación'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};


